// DE profile › Settings (managers; coverage row 30): name, template, start date and the Hermes
// runtime, saved with `PATCH /digital-employees/{id}` (only the changed fields are sent). The form
// is seeded once and re-seeded from the saved profile, so a background refetch never overwrites
// what is typed. Status moves live in the page header (de-lifecycle.tsx).
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  InfoTip,
  Input,
  SaveDock,
  SaveIndicator,
  SettingsAside,
  SettingsLayout,
  SettingsSection,
  workerName,
} from "@companyos/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarIcon, CpuIcon, IdCardIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { DeProfile } from "../../api/types";
import { keys } from "../queries";
import { useDeNames } from "./de-name";
import { problemText } from "./de-status";

type Form = {
  name: string;
  /** HELPERS: the optional person name (offered when the company names DEs by person). */
  personName: string;
  template: string;
  startedOn: string;
  instance: string;
  profile: string;
};

const formOf = (de: DeProfile): Form => ({
  name: de.display_name,
  personName: de.person_name ?? "",
  template: de.template_key,
  startedOn: de.started_on ?? "",
  instance: de.runtime_ref?.instance_id ?? "",
  profile: de.runtime_ref?.profile_name ?? "",
});

export function DeSettingsTab({
  api,
  companyId,
  de,
  operator,
}: {
  api: Api;
  companyId: string;
  de: DeProfile;
  operator: boolean;
}) {
  const qc = useQueryClient();
  const deNames = useDeNames(api, companyId);
  const initial = formOf(de);
  const [form, setForm] = useState<Form>(initial);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const set = (patch: Partial<Form>) => setForm((f) => ({ ...f, ...patch }));
  const dirty = (Object.keys(form) as (keyof Form)[]).some((k) => form[k] !== initial[k]);
  const runtimeHalf = !form.instance.trim() !== !form.profile.trim();
  const errors = {
    name: form.name.trim() ? null : "A name is required.",
    template: form.template.trim() ? null : "A template key is required.",
    runtime: runtimeHalf ? "Give both the instance and the profile, or neither." : null,
  };
  const valid = !errors.name && !errors.template && !errors.runtime;

  const save = useMutation({
    mutationFn: () => {
      const runtimeChanged = form.instance !== initial.instance || form.profile !== initial.profile;
      const instance = form.instance.trim();
      return api.client.digitalEmployees.update({
        params: { co: companyId, id: de.membership_id },
        body: {
          ...(form.name !== initial.name ? { display_name: form.name.trim() } : {}),
          ...(form.personName !== initial.personName
            ? { person_name: form.personName.trim() || null }
            : {}),
          ...(form.template !== initial.template ? { template_key: form.template.trim() } : {}),
          ...(form.startedOn !== initial.startedOn ? { started_on: form.startedOn || null } : {}),
          ...(runtimeChanged
            ? {
                runtime_ref: instance
                  ? {
                      ...(de.runtime_ref ?? {}),
                      system: "hermes" as const,
                      instance_id: instance,
                      profile_name: form.profile.trim(),
                    }
                  : null,
              }
            : {}),
        },
      });
    },
    onSuccess: (profile) => {
      qc.setQueryData(keys.de(companyId, de.membership_id), profile);
      void qc.invalidateQueries({ queryKey: keys.digitalEmployees(companyId) });
      setForm(formOf(profile));
      setSavedAt(Date.now());
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (valid && dirty) save.mutate();
      }}
      aria-label={`${workerName(de.display_name)} settings`}
    >
      <SettingsLayout
        aside={
          <SettingsAside title="About these settings">
            The name shows on scorecards and in the Friday report. The template seeded the seat's
            metric set when the DE was created; changing it later does not change the metrics.
            Pause, resume and retire are in the page header.
          </SettingsAside>
        }
      >
        <SettingsSection
          icon={IdCardIcon}
          title="Identity"
          actions={<SaveIndicator savedAt={savedAt} />}
        >
          <Field data-invalid={errors.name ? true : undefined}>
            <FieldLabel htmlFor="de-name">Name</FieldLabel>
            <Input
              id="de-name"
              value={form.name}
              aria-invalid={errors.name ? true : undefined}
              onChange={(e) => set({ name: e.target.value })}
            />
            {errors.name ? <FieldError>{errors.name}</FieldError> : null}
          </Field>
          {deNames === "person" || initial.personName ? (
            <Field>
              <FieldLabel htmlFor="de-person-name">Person name</FieldLabel>
              <Input
                id="de-person-name"
                maxLength={80}
                placeholder="Maya"
                value={form.personName}
                onChange={(e) => set({ personName: e.target.value })}
              />
              <FieldDescription>Shown before the role. Blank: the role only.</FieldDescription>
            </Field>
          ) : null}
          <Field data-invalid={errors.template ? true : undefined}>
            <FieldLabel htmlFor="de-template">Template key</FieldLabel>
            <Input
              id="de-template"
              className="font-mono"
              value={form.template}
              aria-invalid={errors.template ? true : undefined}
              onChange={(e) => set({ template: e.target.value })}
            />
            {errors.template ? <FieldError>{errors.template}</FieldError> : null}
          </Field>
        </SettingsSection>
        <SettingsSection icon={CalendarIcon} title="Start date">
          <Field>
            <FieldLabel htmlFor="de-started">Started on</FieldLabel>
            <Input
              id="de-started"
              type="date"
              value={form.startedOn}
              onChange={(e) => set({ startedOn: e.target.value })}
            />
            <FieldDescription>The day counter on the profile counts from here.</FieldDescription>
          </Field>
        </SettingsSection>
        {/* WF-FIX: a self-hosted DE's runtime is its lead profile's (ops records it), read-only
            here; the managed runtime_ref editor would show a second, different source. */}
        {de.self_hosted ? (
          <SettingsSection icon={CpuIcon} title="Runtime" data-slot="de-runtime-self-hosted">
            <p className="inline-flex items-center gap-1.5 text-sm">
              {de.self_hosted.runtime
                ? `Hermes · ${de.self_hosted.runtime.container} · ${de.self_hosted.runtime.profile}`
                : "Self-hosted on Hermes"}
              <InfoTip label="About a self-hosted runtime">
                Recorded by ops from the ClawPanel container. Change it there, not here.
              </InfoTip>
            </p>
          </SettingsSection>
        ) : (
          <SettingsSection
            icon={CpuIcon}
            title="Runtime"
            description={
              operator
                ? "The Hermes instance and profile this DE runs as."
                : "The Hermes instance and profile this DE runs as. Usually set by your CompanyOS operator."
            }
          >
            <div className="grid gap-5 sm:grid-cols-2">
              <Field data-invalid={errors.runtime ? true : undefined}>
                <FieldLabel htmlFor="de-instance">Instance</FieldLabel>
                <Input
                  id="de-instance"
                  className="font-mono"
                  placeholder="hermes-eu-1"
                  value={form.instance}
                  onChange={(e) => set({ instance: e.target.value })}
                />
              </Field>
              <Field data-invalid={errors.runtime ? true : undefined}>
                <FieldLabel htmlFor="de-profile">Profile</FieldLabel>
                <Input
                  id="de-profile"
                  className="font-mono"
                  value={form.profile}
                  onChange={(e) => set({ profile: e.target.value })}
                />
              </Field>
            </div>
            {errors.runtime ? <FieldError>{errors.runtime}</FieldError> : null}
          </SettingsSection>
        )}
        {save.isError ? (
          <p role="alert" className="text-sm text-danger-text">
            {problemText(save.error, "The settings could not be saved.")}
          </p>
        ) : null}
      </SettingsLayout>
      <SaveDock
        dirty={dirty}
        saving={save.isPending}
        onSave={() => {
          if (valid) save.mutate();
        }}
        onDiscard={() => {
          setForm(initial);
          save.reset();
        }}
      />
    </form>
  );
}
